import React, {
  useState,
  useEffect,
  useReducer,
  useContext,
  useRef,
} from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  useMediaQuery,
  Table, TableBody, TableCell, TableHead, TableRow,
  Paper, Button, Avatar, Dialog, DialogContent, DialogTitle,
  IconButton, TextField, InputAdornment, Checkbox, Toolbar,
  Typography, Tooltip, Menu, MenuItem, Fab,
} from "@material-ui/core";
import { Facebook, Instagram, WhatsApp, Close } from "@material-ui/icons";
import SearchIcon from "@material-ui/icons/Search";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import EditIcon from "@material-ui/icons/Edit";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import BlockIcon from "@material-ui/icons/Block";
import AddIcon from "@material-ui/icons/Add";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import { alpha } from "@material-ui/core/styles";
import { ArrowDropDown, Backup, ContactPhone, DeleteSweep } from "@material-ui/icons";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactModal from "../../components/ContactModal";
import ConfirmationModal from "../../components/ConfirmationModal/";
import ContactDeleteConfirmModal from "../../components/ContactDeleteConfirmModal";
import { i18n } from "../../translate/i18n";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";
import NewTicketModal from "../../components/NewTicketModal";
import { TagsFilter } from "../../components/TagsFilter";
import PopupState, { bindTrigger, bindMenu } from "material-ui-popup-state";
import formatSerializedId from "../../utils/formatSerializedId";
import { v4 as uuidv4 } from "uuid";
import ContactImportWpModal from "../../components/ContactImportWpModal";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { TicketsContext } from "../../context/Tickets/TicketsContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;
    const newContacts = [];
    contacts.forEach((contact) => {
      const contactIndex = state.findIndex((c) => c.id === contact.id);
      if (contactIndex !== -1) {
        state[contactIndex] = contact;
      } else {
        newContacts.push(contact);
      }
    });
    return [...state, ...newContacts];
  }
  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contact.id);
    if (contactIndex !== -1) {
      state[contactIndex] = contact;
      return [...state];
    } else {
      return [contact, ...state];
    }
  }
  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contactId);
    if (contactIndex !== -1) state.splice(contactIndex, 1);
    return [...state];
  }
  if (action.type === "BULK_DELETE_CONTACTS") {
    const contactIds = action.payload;
    return state.filter(contact => !contactIds.includes(contact.id));
  }
  if (action.type === "DELETE_ALL_CONTACTS") {
    const { excludeIds = [] } = action.payload;
    return state.filter(contact => excludeIds.includes(contact.id));
  }
  if (action.type === "RESET") return [];
  if (action.type === "SET_TOTAL_COUNT") return state;
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0),
    },
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(1),
    },
  },

  // ── CARDS MÓVIL ──────────────────────────────────────────────────────
  cardList: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    padding: theme.spacing(1),
  },

  card: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 14px",
    borderRadius: 14,
    border: "1px solid",
    borderColor: theme.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.07)",
    background: theme.mode === "dark"
      ? "linear-gradient(135deg, rgba(15,23,42,0.95) 0%, rgba(22,32,52,0.90) 100%)"
      : "#ffffff",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    transition: "all 0.18s ease",
    position: "relative",
    "&:hover": {
      boxShadow: "0 4px 12px rgba(91,45,142,0.12)",
      borderColor: "rgba(91,45,142,0.30)",
    },
  },

  cardSelected: {
    borderColor: "#5B2D8E",
    background: theme.mode === "dark"
      ? "rgba(91,45,142,0.12)"
      : "rgba(91,45,142,0.05)",
  },

  cardAvatar: {
    width: 48,
    height: 48,
    flexShrink: 0,
    cursor: "pointer",
    border: "2px solid rgba(91,45,142,0.20)",
  },

  cardBody: {
    flex: 1,
    minWidth: 0,
  },

  cardName: {
    fontWeight: 700,
    fontSize: 14,
    color: theme.mode === "dark" ? "#e2e8f0" : "#1e293b",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "flex",
    alignItems: "center",
    gap: 5,
  },

  cardNumber: {
    fontSize: 12,
    color: theme.mode === "dark" ? "#64748b" : "#94a3b8",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    marginTop: 2,
  },

  cardEmail: {
    fontSize: 11,
    color: theme.mode === "dark" ? "#475569" : "#94a3b8",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  cardActions: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
    gap: 2,
  },

  cardActionBtn: {
    width: 34,
    height: 34,
    padding: 0,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid",
    borderColor: theme.mode === "dark" ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)",
    background: "transparent",
    cursor: "pointer",
    transition: "all 0.15s ease",
    "&:hover": {
      background: "rgba(91,45,142,0.10)",
      borderColor: "rgba(91,45,142,0.35)",
    },
  },

  statusDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    flexShrink: 0,
    marginRight: 2,
  },

  // ── TABLA DESKTOP ────────────────────────────────────────────────────
  avatarCell: { width: 60, padding: theme.spacing(1) },
  idCell: { width: 80 },
  checkboxCell: { width: 48, padding: theme.spacing(0, 1) },
  clickableAvatar: {
    cursor: "pointer",
    "&:hover": { opacity: 0.82 },
  },

  // ── IMAGEN MODAL ─────────────────────────────────────────────────────
  imageDialog: {
    "& .MuiDialog-paper": { maxWidth: 500, maxHeight: 500 },
  },
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingBottom: theme.spacing(1),
  },
  profileImage: {
    width: "100%",
    height: "auto",
    maxWidth: 400,
    maxHeight: 400,
    objectFit: "contain",
  },

  // ── TOOLBAR SELECCIÓN ────────────────────────────────────────────────
  toolbar: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    minHeight: 48,
    borderRadius: 10,
    margin: "0 8px 8px",
  },
  toolbarHighlight: {
    backgroundColor: alpha(theme.palette.primary.main, 0.18),
  },
  toolbarTitle: {
    flex: "1 1 100%",
    fontWeight: 600,
    fontSize: 13,
  },
  bulkActions: {
    display: "flex",
    gap: theme.spacing(1),
  },

  // ── FAB MÓVIL ────────────────────────────────────────────────────────
  fab: {
    position: "fixed",
    bottom: 24,
    right: 24,
    background: "linear-gradient(135deg, #5B2D8E, #7C3AED)",
    color: "#fff",
    zIndex: 1200,
    boxShadow: "0 4px 16px rgba(91,45,142,0.40)",
    "&:hover": {
      background: "linear-gradient(135deg, #0280d9, #1eb8b0)",
    },
  },

  // ── HEADER MÓVIL ────────────────────────────────────────────────────
  mobileHeader: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    padding: "8px 8px 0",
  },

  mobileSearchRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  mobileSearchField: {
    flex: 1,
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      height: 40,
    },
    "& .MuiOutlinedInput-input": {
      padding: "0 14px",
      fontSize: 13,
    },
  },
}));

const Contacts = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user, socket } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, dispatch] = useReducer(reducer, []);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [totalContactsCount, setTotalContactsCount] = useState(0);

  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const [selectAllMode, setSelectAllMode] = useState(false);

  const [importContactModalOpen, setImportContactModalOpen] = useState(false);
  const [deletingContact, setDeletingContact] = useState(null);
  // Borrado completo por solicitud de la persona (ARCO): contacto, chats y archivos.
  const [erasingContact, setErasingContact] = useState(null);
  const [ImportContacts, setImportContacts] = useState(null);
  const [blockingContact, setBlockingContact] = useState(null);
  const [unBlockingContact, setUnBlockingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [exportContact, setExportContact] = useState(false);
  const [confirmChatsOpen, setConfirmChatsOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [contactTicket, setContactTicket] = useState({});

  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [deleteType, setDeleteType] = useState("");

  const fileUploadRef = useRef(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const { setCurrentTicket } = useContext(TicketsContext);

  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [selectedContactName, setSelectedContactName] = useState("");

  // Menú de opciones por contacto en móvil
  const [cardMenuAnchor, setCardMenuAnchor] = useState(null);
  const [cardMenuContact, setCardMenuContact] = useState(null);

  const { getAll: getAllSettings } = useCompanySettings();
  const [hideNum, setHideNum] = useState(false);
  const [enableLGPD, setEnableLGPD] = useState(false);

  useEffect(() => {
    async function fetchData() {
      const settingList = await getAllSettings(user.companyId);
      for (const [key, value] of Object.entries(settingList)) {
        if (key === "enableLGPD") setEnableLGPD(value === "enabled");
        if (key === "lgpdHideNumber") setHideNum(value === "enabled");
      }
    }
    fetchData();
  }, []);

  const handleSelectContact = (contactId) => {
    const newSelected = new Set(selectedContacts);
    if (newSelected.has(contactId)) {
      newSelected.delete(contactId);
    } else {
      newSelected.add(contactId);
    }
    setSelectedContacts(newSelected);
    setSelectAllMode(false);
  };

  const handleSelectAllContacts = () => {
    if (selectAllMode) {
      setSelectedContacts(new Set());
      setSelectAllMode(false);
    } else {
      const currentPageIds = contacts.map((c) => c.id);
      const allCurrentSelected = currentPageIds.every((id) => selectedContacts.has(id));
      if (allCurrentSelected && selectedContacts.size === currentPageIds.length) {
        setSelectAllMode(true);
        setSelectedContacts(new Set());
      } else {
        setSelectedContacts(new Set(currentPageIds));
        setSelectAllMode(false);
      }
    }
  };

  useEffect(() => {
    setSelectedContacts(new Set());
    setSelectAllMode(false);
  }, [searchParam, selectedTags]);

  const getSelectedCount = () => selectAllMode ? totalContactsCount : selectedContacts.size;

  const getSelectionButtonText = () => {
    if (selectAllMode) return `Eliminar Todos (${totalContactsCount})`;
    if (selectedContacts.size > 0) return `Eliminar (${selectedContacts.size})`;
    return "Eliminar";
  };

  const handleBulkDeleteClick = () => {
    if (selectAllMode) {
      setDeleteType("all");
    } else if (selectedContacts.size > 0) {
      setDeleteType("selected");
    } else {
      toast.warning("Selecciona al menos un contacto");
      return;
    }
    setDeleteConfirmModalOpen(true);
  };

  const handleBulkDeleteConfirm = async () => {
    try {
      if (deleteType === "all") {
        await api.delete("/contacts/all", {
          data: { confirmation: "DELETE_ALL_CONTACTS", excludeIds: [] },
        });
        toast.success(`Todos os ${totalContactsCount} contatos foram excluídos`);
      } else {
        const contactIds = Array.from(selectedContacts);
        await api.post("/contacts/bulk-delete", { contactIds });
        toast.success(`${contactIds.length} contatos excluídos com sucesso`);
      }
      setSelectedContacts(new Set());
      setSelectAllMode(false);
      setSearchParam("");
      setPageNumber(1);
    } catch (err) {
      toastError(err);
    } finally {
      setDeleteConfirmModalOpen(false);
      setDeleteType("");
    }
  };

  const handleDeleteAllClick = () => {
    setDeleteType("all");
    setDeleteConfirmModalOpen(true);
  };

  const handleOpenImageModal = (imageUrl, contactName) => {
    setSelectedImage(imageUrl);
    setSelectedContactName(contactName);
    setImageModalOpen(true);
  };

  const handleCloseImageModal = () => {
    setImageModalOpen(false);
    setSelectedImage("");
    setSelectedContactName("");
  };

  const handleImportExcel = async () => {
    try {
      const formData = new FormData();
      formData.append("file", fileUploadRef.current.files[0]);
      await api.request({ url: `/contacts/upload`, method: "POST", data: formData });
      history.go(0);
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, selectedTags]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("/contacts/", {
            params: { searchParam, pageNumber, contactTag: JSON.stringify(selectedTags) },
          });
          dispatch({ type: "LOAD_CONTACTS", payload: data.contacts });
          setHasMore(data.hasMore);
          setTotalContactsCount(data.count);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, selectedTags]);

  useEffect(() => {
    const companyId = user.companyId;
    const onContactEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTS", payload: data.contact });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.contactId });
        setTotalContactsCount((prev) => Math.max(0, prev - 1));
      }
      if (data.action === "bulk-delete") {
        dispatch({ type: "BULK_DELETE_CONTACTS", payload: data.contactIds });
        setTotalContactsCount((prev) => Math.max(0, prev - data.contactIds.length));
      }
      if (data.action === "delete-all") {
        dispatch({ type: "DELETE_ALL_CONTACTS", payload: { excludeIds: data.excludeIds } });
        setTotalContactsCount(data.excludeIds.length);
      }
    };
    socket.on(`company-${companyId}-contact`, onContactEvent);
    return () => socket.off(`company-${companyId}-contact`, onContactEvent);
  }, [socket]);

  const handleSelectTicket = (ticket) => {
    const code = uuidv4();
    const { id, uuid } = ticket;
    setCurrentTicket({ id, uuid, code });
  };

  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      handleSelectTicket(ticket);
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  const handleSelectedTags = (selecteds) => {
    setSelectedTags(selecteds.map((t) => t.id));
  };

  const handleSearch = (event) => setSearchParam(event.target.value.toLowerCase());

  const handleOpenContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(false);
  };

  const hadleEditContact = (contactId) => {
    setSelectedContactId(contactId);
    setContactModalOpen(true);
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await api.delete(`/contacts/${contactId}`);
      toast.success(i18n.t("contacts.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const handleEraseContact = async (contactId) => {
    try {
      const { data } = await api.delete(`/contacts/${contactId}/erase`);
      toast.success(
        `Datos borrados: ${data.tickets} conversaciones, ${data.messages} mensajes y ${data.files} archivos`
      );
    } catch (err) {
      toastError(err);
    }
    setErasingContact(null);
    setSearchParam("");
    setPageNumber(1);
  };

  // Al cerrar el modal se olvida a quien iba dirigido. Antes se quedaba
  // guardado: cancelar un borrado y luego pulsar "Bloquear" en otro contacto
  // confirmaba el borrado del primero.
  const handleConfirmClose = (open) => {
    setConfirmOpen(open);
    if (!open) {
      setDeletingContact(null);
      setErasingContact(null);
      setBlockingContact(null);
      setUnBlockingContact(null);
    }
  };

  const handleBlockContact = async (contactId) => {
    try {
      await api.put(`/contacts/block/${contactId}`, { active: false });
      toast.success("Contacto bloqueado");
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
    setSearchParam("");
    setPageNumber(1);
    setBlockingContact(null);
  };

  const handleUnBlockContact = async (contactId) => {
    try {
      await api.put(`/contacts/block/${contactId}`, { active: true });
      toast.success("Contacto desbloqueado");
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
    setSearchParam("");
    setPageNumber(1);
    setUnBlockingContact(null);
  };

  const handleimportContact = async () => {
    try {
      await api.post("/contacts/import");
      history.go(0);
      setImportContacts(false);
    } catch (err) {
      toastError(err);
      setImportContacts(false);
    }
  };

  const handleimportChats = async () => {
    try {
      await api.post("/contacts/import/chats");
      history.go(0);
    } catch (err) {
      toastError(err);
    }
  };

  const loadMore = () => setPageNumber((prev) => prev + 1);

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) loadMore();
  };

  const getConfirmAction = () => {
    if (erasingContact) return () => handleEraseContact(erasingContact.id);
    if (deletingContact) return () => handleDeleteContact(deletingContact.id);
    if (blockingContact) return () => handleBlockContact(blockingContact.id);
    if (unBlockingContact) return () => handleUnBlockContact(unBlockingContact.id);
    if (ImportContacts) return handleimportContact;
    return handleImportExcel;
  };

  const getConfirmTitle = () => {
    if (erasingContact) return `¿Borrar todos los datos de ${erasingContact.name}?`;
    if (deletingContact) return `${i18n.t("contacts.confirmationModal.deleteTitle")} ${deletingContact.name}?`;
    if (blockingContact) return `Bloquear Contacto ${blockingContact.name}?`;
    if (unBlockingContact) return `Desbloquear Contacto ${unBlockingContact.name}?`;
    if (ImportContacts) return i18n.t("contacts.confirmationModal.importTitlte");
    return i18n.t("contactListItems.confirmationModal.importTitlte");
  };

  const getConfirmMessage = () => {
    if (exportContact) return i18n.t("contacts.confirmationModal.exportContact");
    if (erasingContact)
      return "Se borran para siempre el contacto, todas sus conversaciones, los archivos que envió o recibió, " +
        "sus envíos por API y su lugar en las listas de campañas. Úsalo cuando la persona pida que borres sus datos. " +
        "No se puede deshacer, y no borra lo que ya está en WhatsApp, Instagram o Facebook.";
    if (deletingContact) return i18n.t("contacts.confirmationModal.deleteMessage");
    if (blockingContact) return i18n.t("contacts.confirmationModal.blockContact");
    if (unBlockingContact) return i18n.t("contacts.confirmationModal.unblockContact");
    if (ImportContacts) return i18n.t("contacts.confirmationModal.importMessage");
    return i18n.t("contactListItems.confirmationModal.importMessage");
  };

  const formatNumber = (contact) => {
    if (enableLGPD && hideNum && user.profile === "user") {
      return contact.isGroup
        ? contact.number
        : formatSerializedId(contact?.number) === null
        ? contact.number.slice(0, -6) + "**-**" + contact?.number.slice(-2)
        : formatSerializedId(contact?.number)?.slice(0, -6) + "**-**" + contact?.number?.slice(-2);
    }
    return contact.isGroup ? contact.number : formatSerializedId(contact?.number);
  };

  const getChannelIcon = (contact) => {
    if (contact.channel === "whatsapp") return <WhatsApp style={{ fontSize: 16, color: "#25D366" }} />;
    if (contact.channel === "instagram") return <Instagram style={{ fontSize: 16, color: "#E1306C" }} />;
    if (contact.channel === "facebook") return <Facebook style={{ fontSize: 16, color: "#4267B2" }} />;
    return null;
  };

  const selectedCount = getSelectedCount();
  const isAnyContactSelected = selectAllMode || selectedContacts.size > 0;

  const selectAllCheckboxStatus = () => {
    if (selectAllMode) return { checked: true, indeterminate: false };
    const currentPageIds = contacts.map((c) => c.id);
    const selectedInCurrentPage = currentPageIds.filter((id) => selectedContacts.has(id)).length;
    if (selectedInCurrentPage === 0) return { checked: false, indeterminate: false };
    if (selectedInCurrentPage === currentPageIds.length) return { checked: true, indeterminate: false };
    return { checked: false, indeterminate: true };
  };

  const checkboxStatus = selectAllCheckboxStatus();

  // ── RENDER CARD MÓVIL ──────────────────────────────────────────────
  const renderMobileCard = (contact) => {
    const isSelected = selectAllMode || selectedContacts.has(contact.id);
    return (
      <div
        key={contact.id}
        className={`${classes.card} ${isSelected ? classes.cardSelected : ""}`}
      >
        {/* Checkbox */}
        <Checkbox
          checked={isSelected}
          onChange={() => handleSelectContact(contact.id)}
          disabled={selectAllMode}
          size="small"
          style={{ padding: 0, marginRight: -4 }}
        />

        {/* Avatar */}
        <Avatar
          src={contact?.urlPicture}
          className={classes.cardAvatar}
          onClick={() => handleOpenImageModal(contact?.urlPicture, contact.name)}
        >
          {contact.name?.[0]?.toUpperCase()}
        </Avatar>

        {/* Info */}
        <div className={classes.cardBody}>
          <div className={classes.cardName}>
            {getChannelIcon(contact)}
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {contact.name}
            </span>
            <span style={{
              width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
              background: contact.active ? "#22c55e" : "#ef4444",
              marginLeft: 2,
            }} />
          </div>
          <div className={classes.cardNumber}>{formatNumber(contact)}</div>
          {contact.email ? (
            <div className={classes.cardEmail}>{contact.email}</div>
          ) : null}
        </div>

        {/* Acciones */}
        <div className={classes.cardActions}>
          {/* Botón canal (nuevo ticket) */}
          <IconButton
            size="small"
            disabled={!contact.active}
            onClick={() => {
              setContactTicket(contact);
              setNewTicketModalOpen(true);
            }}
            style={{ padding: 6 }}
          >
            {getChannelIcon(contact)}
          </IconButton>

          {/* Menú de más opciones */}
          <IconButton
            size="small"
            onClick={(e) => {
              setCardMenuAnchor(e.currentTarget);
              setCardMenuContact(contact);
            }}
            style={{ padding: 6 }}
          >
            <MoreVertIcon style={{ fontSize: 18 }} />
          </IconButton>
        </div>
      </div>
    );
  };

  return (
    <MainContainer>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        initialContact={contactTicket}
        onClose={(ticket) => handleCloseOrOpenTicket(ticket)}
      />
      <ContactModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        contactId={selectedContactId}
      />

      <ContactDeleteConfirmModal
        open={deleteConfirmModalOpen}
        onClose={() => setDeleteConfirmModalOpen(false)}
        onConfirm={handleBulkDeleteConfirm}
        deleteType={deleteType}
        selectedCount={selectedContacts.size}
        totalCount={totalContactsCount}
      />

      <Dialog
        open={imageModalOpen}
        onClose={handleCloseImageModal}
        className={classes.imageDialog}
        maxWidth="md"
      >
        <DialogTitle className={classes.dialogTitle}>
          <span>Foto de Perfil — {selectedContactName}</span>
          <IconButton onClick={handleCloseImageModal} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedImage ? (
            <img
              src={selectedImage}
              alt={`Foto de perfil de ${selectedContactName}`}
              className={classes.profileImage}
              onError={(e) => { e.target.style.display = "none"; }}
            />
          ) : (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200, color: "#666" }}>
              Imagen no disponible
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmationModal
        title={getConfirmTitle()}
        open={confirmOpen}
        onClose={handleConfirmClose}
        onConfirm={getConfirmAction()}
      >
        {getConfirmMessage()}
      </ConfirmationModal>

      <ConfirmationModal
        title={i18n.t("contacts.confirmationModal.importChat")}
        open={confirmChatsOpen}
        onClose={setConfirmChatsOpen}
        onConfirm={handleimportChats}
      >
        {i18n.t("contacts.confirmationModal.wantImport")}
      </ConfirmationModal>

      {/* Menú contextual por card en móvil */}
      <Menu
        anchorEl={cardMenuAnchor}
        open={Boolean(cardMenuAnchor)}
        onClose={() => { setCardMenuAnchor(null); setCardMenuContact(null); }}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={() => {
          hadleEditContact(cardMenuContact?.id);
          setCardMenuAnchor(null);
        }}>
          <EditIcon fontSize="small" style={{ marginRight: 10, color: "#5B2D8E" }} />
          Editar
        </MenuItem>
        <MenuItem onClick={() => {
          setConfirmOpen(true);
          if (cardMenuContact?.active) {
            setBlockingContact(cardMenuContact);
          } else {
            setUnBlockingContact(cardMenuContact);
          }
          setCardMenuAnchor(null);
        }}>
          {cardMenuContact?.active
            ? <BlockIcon fontSize="small" style={{ marginRight: 10, color: "#f59e0b" }} />
            : <CheckCircleIcon fontSize="small" style={{ marginRight: 10, color: "#22c55e" }} />}
          {cardMenuContact?.active ? "Bloquear" : "Desbloquear"}
        </MenuItem>
        <Can
          role={user.profile}
          perform="contacts-page:deleteContact"
          yes={() => (
            <MenuItem onClick={() => {
              setConfirmOpen(true);
              setDeletingContact(cardMenuContact);
              setCardMenuAnchor(null);
            }} style={{ color: "#ef4444" }}>
              <DeleteOutlineIcon fontSize="small" style={{ marginRight: 10 }} />
              Eliminar
            </MenuItem>
          )}
        />
        <Can
          role={user.profile}
          perform="contacts-page:eraseContactData"
          yes={() => (
            <MenuItem onClick={() => {
              setConfirmOpen(true);
              setErasingContact(cardMenuContact);
              setCardMenuAnchor(null);
            }} style={{ color: "#b91c1c" }}>
              <DeleteForeverIcon fontSize="small" style={{ marginRight: 10 }} />
              Borrar todos sus datos
            </MenuItem>
          )}
        />
      </Menu>

      {/* ── HEADER ──────────────────────────────────────────────────── */}
      {isMobile ? (
        // Header móvil compacto
        <div className={classes.mobileHeader}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="h6" style={{ fontWeight: 700, fontSize: 16 }}>
              Contactos
              <span style={{ fontSize: 13, fontWeight: 400, color: "#94a3b8", marginLeft: 6 }}>
                ({totalContactsCount})
              </span>
            </Typography>
            <div style={{ display: "flex", gap: 6 }}>
              <PopupState variant="popover" popupId="mobile-import-menu">
                {(popupState) => (
                  <>
                    <IconButton size="small" {...bindTrigger(popupState)}>
                      <Backup fontSize="small" />
                    </IconButton>
                    <Menu {...bindMenu(popupState)}>
                      <MenuItem onClick={() => {
                        setConfirmOpen(true);
                        setImportContacts(true);
                        popupState.close();
                      }}>
                        <ContactPhone fontSize="small" style={{ marginRight: 10 }} />
                        Importar del teléfono
                      </MenuItem>
                      <MenuItem onClick={() => {
                        setImportContactModalOpen(true);
                        popupState.close();
                      }}>
                        <Backup fontSize="small" style={{ marginRight: 10 }} />
                        Importar Excel
                      </MenuItem>
                      <Can
                        role={user.profile}
                        perform="contacts-page:deleteAllContacts"
                        yes={() => (
                          <MenuItem onClick={() => {
                            handleDeleteAllClick();
                            popupState.close();
                          }} style={{ color: "#ef4444" }}>
                            <DeleteSweep fontSize="small" style={{ marginRight: 10 }} />
                            Eliminar todos
                          </MenuItem>
                        )}
                      />
                    </Menu>
                  </>
                )}
              </PopupState>
            </div>
          </div>

          <div className={classes.mobileSearchRow}>
            <TextField
              variant="outlined"
              placeholder={i18n.t("contacts.searchPlaceholder")}
              type="search"
              value={searchParam}
              onChange={handleSearch}
              className={classes.mobileSearchField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ fontSize: 18, color: "#94a3b8" }} />
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <TagsFilter onFiltered={handleSelectedTags} />
        </div>
      ) : (
        // Header desktop
        <MainHeader>
          <Title>
            {i18n.t("contacts.title")} ({totalContactsCount})
            {isAnyContactSelected && (
              <span style={{ color: "#f50057", marginLeft: 8 }}>
                — {selectedCount} seleccionado(s) {selectAllMode && "(TODOS)"}
              </span>
            )}
          </Title>
          <MainHeaderButtonsWrapper>
            <TagsFilter onFiltered={handleSelectedTags} />
            <TextField
              placeholder={i18n.t("contacts.searchPlaceholder")}
              type="search"
              value={searchParam}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="secondary" />
                  </InputAdornment>
                ),
              }}
            />
            {isAnyContactSelected && (
              <Tooltip title={selectAllMode ? "Eliminar todos los contactos" : "Eliminar seleccionados"}>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={handleBulkDeleteClick}
                >
                  {getSelectionButtonText()}
                </Button>
              </Tooltip>
            )}
            <PopupState variant="popover" popupId="desktop-import-menu">
              {(popupState) => (
                <>
                  <Button variant="contained" color="primary" {...bindTrigger(popupState)}>
                    {i18n.t("contacts.menu.importexport")}
                    <ArrowDropDown />
                  </Button>
                  <Menu {...bindMenu(popupState)}>
                    <MenuItem onClick={() => {
                      setConfirmOpen(true);
                      setImportContacts(true);
                      popupState.close();
                    }}>
                      <ContactPhone fontSize="small" color="primary" style={{ marginRight: 10 }} />
                      {i18n.t("contacts.menu.importYourPhone")}
                    </MenuItem>
                    <MenuItem onClick={() => {
                      setImportContactModalOpen(true);
                      popupState.close();
                    }}>
                      <Backup fontSize="small" color="primary" style={{ marginRight: 10 }} />
                      {i18n.t("contacts.menu.importToExcel")}
                    </MenuItem>
                    <Can
                      role={user.profile}
                      perform="contacts-page:deleteAllContacts"
                      yes={() => (
                        <MenuItem onClick={() => {
                          handleDeleteAllClick();
                          popupState.close();
                        }} style={{ color: "#f44336" }}>
                          <DeleteSweep fontSize="small" style={{ marginRight: 10, color: "#f44336" }} />
                          Eliminar Todos los Contactos
                        </MenuItem>
                      )}
                    />
                  </Menu>
                </>
              )}
            </PopupState>
            <Button variant="contained" color="primary" onClick={handleOpenContactModal}>
              {i18n.t("contacts.buttons.add")}
            </Button>
          </MainHeaderButtonsWrapper>
        </MainHeader>
      )}

      {importContactModalOpen && (
        <ContactImportWpModal
          isOpen={importContactModalOpen}
          handleClose={() => setImportContactModalOpen(false)}
          selectedTags={selectedTags}
          hideNum={hideNum}
          userProfile={user.profile}
        />
      )}

      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        <input
          style={{ display: "none" }}
          id="upload"
          name="file"
          type="file"
          accept=".xls,.xlsx"
          onChange={() => setConfirmOpen(true)}
          ref={fileUploadRef}
        />

        {/* Toolbar de selección múltiple */}
        {isAnyContactSelected && (
          <Toolbar className={`${classes.toolbar} ${classes.toolbarHighlight}`}>
            <Typography className={classes.toolbarTitle} color="inherit" variant="subtitle1">
              {selectedCount} contacto(s) seleccionado(s) {selectAllMode && "(TODOS)"}
            </Typography>
            <div className={classes.bulkActions}>
              <Button size="small" onClick={() => {
                setSelectedContacts(new Set());
                setSelectAllMode(false);
              }}>
                Cancelar
              </Button>
              <IconButton color="inherit" onClick={handleBulkDeleteClick}>
                <DeleteOutlineIcon />
              </IconButton>
            </div>
          </Toolbar>
        )}

        {/* ── VISTA MÓVIL: CARDS ───────────────────────────────────── */}
        {isMobile ? (
          <div className={classes.cardList}>
            {contacts.map((contact) => renderMobileCard(contact))}
            {loading && (
              <div style={{ textAlign: "center", padding: 16, color: "#94a3b8", fontSize: 13 }}>
                Cargando...
              </div>
            )}
          </div>
        ) : (
          // ── VISTA DESKTOP: TABLA ─────────────────────────────────
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.checkboxCell}>
                  <Checkbox
                    indeterminate={checkboxStatus.indeterminate}
                    checked={checkboxStatus.checked}
                    onChange={handleSelectAllContacts}
                    disabled={contacts.length === 0}
                    inputProps={{ "aria-label": "Seleccionar todos" }}
                  />
                </TableCell>
                <TableCell className={classes.idCell}>ID</TableCell>
                <TableCell className={classes.avatarCell} align="center">Foto</TableCell>
                <TableCell>{i18n.t("contacts.table.name")}</TableCell>
                <TableCell align="center">{i18n.t("contacts.table.whatsapp")}</TableCell>
                <TableCell align="center">{i18n.t("contacts.table.email")}</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">{i18n.t("contacts.table.wallet")}</TableCell>
                <TableCell align="center">{i18n.t("contacts.table.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contacts.map((contact) => {
                const isSelected = selectAllMode || selectedContacts.has(contact.id);
                return (
                  <TableRow key={contact.id} selected={isSelected}>
                    <TableCell className={classes.checkboxCell}>
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleSelectContact(contact.id)}
                        disabled={selectAllMode}
                      />
                    </TableCell>
                    <TableCell className={classes.idCell}>{contact.id}</TableCell>
                    <TableCell className={classes.avatarCell} align="center">
                      <Avatar
                        src={contact?.urlPicture}
                        className={classes.clickableAvatar}
                        onClick={() => handleOpenImageModal(contact?.urlPicture, contact.name)}
                      />
                    </TableCell>
                    <TableCell>{contact.name}</TableCell>
                    <TableCell align="center">{formatNumber(contact)}</TableCell>
                    <TableCell align="center">{contact.email}</TableCell>
                    <TableCell align="center">
                      {contact.active ? (
                        <CheckCircleIcon style={{ color: "green" }} fontSize="small" />
                      ) : (
                        <CancelIcon style={{ color: "red" }} fontSize="small" />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {contact.contactWallets && contact.contactWallets.length > 0
                        ? contact.contactWallets[0].wallet?.name || "Usuario no encontrado"
                        : "No asignado"}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        disabled={!contact.active}
                        onClick={() => {
                          setContactTicket(contact);
                          setNewTicketModalOpen(true);
                        }}
                      >
                        {contact.channel === "whatsapp" && <WhatsApp style={{ color: "green" }} />}
                        {contact.channel === "instagram" && <Instagram style={{ color: "purple" }} />}
                        {contact.channel === "facebook" && <Facebook style={{ color: "blue" }} />}
                      </IconButton>
                      <IconButton size="small" onClick={() => hadleEditContact(contact.id)}>
                        <EditIcon color="secondary" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={
                          contact.active
                            ? () => { setConfirmOpen(true); setBlockingContact(contact); }
                            : () => { setConfirmOpen(true); setUnBlockingContact(contact); }
                        }
                      >
                        {contact.active
                          ? <BlockIcon color="secondary" />
                          : <CheckCircleIcon color="secondary" />}
                      </IconButton>
                      <Can
                        role={user.profile}
                        perform="contacts-page:deleteContact"
                        yes={() => (
                          <IconButton size="small" onClick={() => {
                            setConfirmOpen(true);
                            setDeletingContact(contact);
                          }}>
                            <DeleteOutlineIcon color="secondary" />
                          </IconButton>
                        )}
                      />
                      <Can
                        role={user.profile}
                        perform="contacts-page:eraseContactData"
                        yes={() => (
                          <Tooltip title="Borrar todos sus datos (conversaciones y archivos)">
                            <IconButton size="small" onClick={() => {
                              setConfirmOpen(true);
                              setErasingContact(contact);
                            }}>
                              <DeleteForeverIcon style={{ color: "#b91c1c" }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
              {loading && <TableRowSkeleton avatar columns={9} />}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* FAB para nuevo contacto en móvil */}
      {isMobile && (
        <Fab className={classes.fab} onClick={handleOpenContactModal}>
          <AddIcon />
        </Fab>
      )}
    </MainContainer>
  );
};

export default Contacts;
